import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Trophy, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useMatch } from "@/hooks/useMatch";
import { Pitch, Bench } from "@/components/Pitch";
import { PlayerSheet } from "@/components/PlayerSheet";
import { ScoreBoard } from "@/components/ScoreBoard";
import { MotmVote, MotmWinner } from "@/components/Motm";
import { Avatar } from "@/components/Avatar";
import { fmtDate } from "@/lib/football";

export function HistoryList() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/matches/history").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  return (
    <div data-testid="history-page" className="space-y-4">
      <Link to="/matchs" className="inline-flex items-center gap-1.5 text-sm text-slate-500" data-testid="history-back"><ArrowLeft className="w-4 h-4" /> Retour</Link>
      <h1 className="font-display text-2xl font-extrabold">HISTORIQUE DES MATCHS</h1>
      {items?.length === 0 && <p className="text-sm text-slate-500">Aucun match terminé pour l'instant.</p>}
      <div className="space-y-2.5">
        {items?.map((h) => (
          <Link key={h.id} to={`/matchs/${h.id}`} data-testid={`history-item-${h.id}`} className="block rounded-3xl bg-white shadow-sm border border-slate-200 p-4 active:scale-[0.99] transition-transform">
            <p className="text-sm font-bold">{fmtDate(h.date)}</p>
            {h.status === "cancelled" ? (
              <p className="text-xs text-red-500 mt-1">Match annulé</p>
            ) : (
              <>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-[10px] font-black bg-white text-zinc-950 border border-slate-300 rounded-full px-2.5 py-0.5">BLANC</span>
                  <span className="num text-2xl font-black">{h.white_score} - {h.black_score}</span>
                  <span className="text-[10px] font-black bg-zinc-950 text-white rounded-full px-2.5 py-0.5">NOIR</span>
                </div>
                {h.motm_winner ? <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> Homme du Match : {h.motm_winner.short_name}</p>
                  : h.motm_voting_open && <p className="text-xs text-cyan-600 mt-2">Vote Homme du Match en cours</p>}
              </>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

const Scorers = ({ data }) => {
  const byId = Object.fromEntries(data.players.map((p) => [p.id, p]));
  const rows = data.stats.filter((s) => (s.goals || s.assists) && byId[s.player_id]).sort((a, b) => b.goals - a.goals || b.assists - a.assists);
  if (!rows.length) return null;
  return (
    <div className="rounded-3xl bg-white shadow-sm border border-slate-200 p-4" data-testid="match-scorers">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Buts & passes décisives</p>
      <div className="space-y-2">
        {rows.map((s) => (
          <div key={s.player_id} className="flex items-center gap-3">
            <Avatar user={byId[s.player_id]} className="w-8 h-8 text-[10px]" ring={s.team === "white" ? "border-2 border-slate-300" : "border-2 border-zinc-950"} />
            <span className="flex-1 text-sm font-semibold">{byId[s.player_id].short_name}</span>
            <span className="num text-xs text-slate-600">{s.goals} but{s.goals > 1 ? "s" : ""} · {s.assists} passe{s.assists > 1 ? "s" : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function MatchDetail() {
  const { id } = useParams();
  const { data, act } = useMatch(id);
  const [sel, setSel] = useState(null);
  if (!data) return <div className="h-96 rounded-3xl bg-white shadow-sm animate-pulse" />;
  const m = data.match;
  const byId = Object.fromEntries(data.players.map((p) => [p.id, p]));
  const selPlayer = sel && byId[sel];
  return (
    <div data-testid="match-detail-page" className="space-y-4">
      <Link to="/matchs/historique" className="inline-flex items-center gap-1.5 text-sm text-slate-500" data-testid="detail-back"><ArrowLeft className="w-4 h-4" /> Historique</Link>
      <div>
        <h1 className="font-display text-2xl font-extrabold">{fmtDate(m.date, true)}</h1>
        <p className="text-xs text-slate-500 flex gap-3 mt-0.5"><span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{m.time}</span><span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{m.location}</span><span>Formation {m.formation}</span></p>
        <p className="text-xs text-slate-500 mt-1" data-testid="detail-captains">Capitaines : {byId[m.captain_white]?.short_name || "—"} (Blanc) · {byId[m.captain_black]?.short_name || "—"} (Noir)</p>
      </div>
      {m.status === "cancelled" && <p className="text-sm text-red-500">Ce match a été annulé.</p>}
      <ScoreBoard data={data} />
      <MotmWinner data={data} />
      <MotmVote data={data} act={act} />
      <Pitch data={data} onTap={(p) => setSel(p.id)} selectedId={sel} />
      <Bench data={data} onTap={(p) => setSel(p.id)} />
      <Scorers data={data} />
      <PlayerSheet data={data} player={selPlayer} onClose={() => setSel(null)} act={act} />
    </div>
  );
}
